/**
 * Downlink to a QOTO 02F05 LoRaWAN valve, from QOTO's "LoRaWAN Agreement Interface Document"
 * (V2.52, section 5.2.1, command A4) and its worked examples (the CRCs in them check out):
 *
 *   FE FA 000D | serial (5) | A4 | mission (4) | task type 01 | valve type | angle | CRC-16/MODBUS LE
 *
 * The CRC covers protocol number .. angle. "Serial" and "mission" are the document's names: in
 * its examples both tick up with every command (…3B/…1878, …3C/…1879), so they are command
 * numbers, not the device's serial; the device echoes the mission in its B3/B4 status reports.
 * Valve type: 00 single-pass, 01 three-way, though every example (single-pass included) uses 01.
 * Angle: bit 6-0 the opening 0-100 %; bit 7 picks direction A on a three-way valve.
 *
 * The valve is Class C and reports B4 as soon as its angle changes, which is how the command is
 * confirmed. The document gives no fPort: the queue sends on the port the valve last reported on.
 */
import { crc16Modbus } from "./qoto.ts";
import type { MilesightCommand } from "./milesight-commands.ts";

export const QOTO_MODEL = "qoto-02f05";

export interface QotoValveOptions {
  /** Opening 1-100 % (default fully open); ignored when closing. */
  percent?: number;
  /** Three-way valve: open towards direction A (bit 7); the default is direction B. */
  directionA?: boolean;
  /** True for a three-way valve (valve type 01). The examples use 01 for single-pass too. */
  threeWay?: boolean;
  /** Command number 1..2^32-1, echoed back by the valve in its status reports. */
  mission?: number;
  /** The 5-byte "serial" field (default: the mission number). Only the document's examples set it apart. */
  serial?: number;
}

/** The A4 frame to open (to a percentage) or close the valve. */
export function qotoValveFrame(on: boolean, options: QotoValveOptions = {}): Uint8Array {
  const percent = on ? (options.percent ?? 100) : 0;
  if (!Number.isInteger(percent) || percent < 0 || percent > 100) throw new Error("percent must be 0 to 100");
  const mission = options.mission ?? 0;
  if (!Number.isInteger(mission) || mission < 0 || mission > 0xffffffff) throw new Error("mission must be 0 to 4294967295");
  const serial = options.serial ?? mission;
  if (!Number.isSafeInteger(serial) || serial < 0 || serial > 0xffffffffff) throw new Error("serial must be 0 to 2^40-1");
  const data = Buffer.alloc(13);
  data.writeUIntBE(serial, 0, 5);
  data[5] = 0xa4;
  data.writeUInt32BE(mission >>> 0, 6);
  data[10] = 0x01; // task type
  data[11] = options.threeWay === false ? 0x00 : 0x01;
  data[12] = (percent & 0x7f) | (on && options.directionA ? 0x80 : 0);
  const body = Buffer.concat([Buffer.from([0xfa, 0x00, data.length]), data]);
  const crc = crc16Modbus(body);
  return Uint8Array.from([0xfe, ...body, crc & 0xff, crc >> 8]);
}

/** Open or close a QOTO valve, confirmed when it reports the new opening. */
export function setQotoValve(on: boolean, options: QotoValveOptions = {}): MilesightCommand {
  const bytes = qotoValveFrame(on, options);
  const percent = on ? (options.percent ?? 100) : 0;
  return {
    text: Buffer.from(bytes).toString("hex"),
    bytes,
    // Not given by QOTO: the queue replaces it with the port the valve last reported on.
    fPort: 85,
    fPortFromUplink: true,
    description: on ? `Valve open${percent < 100 ? ` ${percent} %` : ""}${options.directionA ? " (direction A)" : ""}` : "Valve close",
    port: "valve",
    // The valve reports the opening it reached: within a couple of percent of what was asked.
    confirmDecoded: (values) => values.some((v) => v.name === "valve" && (on ? Math.abs((v.value as number) - percent) <= 2 : v.value === 0)),
    // Class C and reports on any angle change; the motor takes a little while to travel.
    confirmTimeoutMs: 3 * 60_000,
  };
}
